import { HubConnectionState } from '@microsoft/signalr';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useAppContext } from '../AppContext';
import { useClientLogContext } from '../ClientLogContext';
import { POSSignalRHub } from '../SignalRConnection';

export type IListingNotificationContext = {
  hubConnection?: POSSignalRHub;
};

export const ListingNotificationContext =
  createContext<IListingNotificationContext>({
    hubConnection: undefined,
  });

export const useListingNotificationContext = () =>
  useContext(ListingNotificationContext);

export function ListingNotificationContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { activeAccountWebClientConfig } = useAppContext();
  const [hubConnection, setHubConnection] = useState<POSSignalRHub>();
  const { logEvent } = useClientLogContext();

  useEffect(() => {
    // Only attempt to connect to signal R when there is a login
    if (activeAccountWebClientConfig.activeAccountId) {
      if (
        !hubConnection ||
        hubConnection.state === HubConnectionState.Disconnected ||
        hubConnection.state === HubConnectionState.Disconnecting
      ) {
        const newHubConnection = new POSSignalRHub(
          '/hubs/listingnotification',
          activeAccountWebClientConfig,
          logEvent
        );

        setHubConnection(newHubConnection);

        newHubConnection
          .start()
          .then(() => {
            console.debug(
              `ListingNotification connection ready with id: ${newHubConnection.connectionId}`
            );
          })
          .catch((err) => {
            console.warn(
              'ListingNotification could not be started at this time: ' + err
            );
          });

        newHubConnection.onclose((err) => {
          console.debug('ListingNotification connection closed', err);
        });
      }
    }

    return () => {
      if (
        hubConnection &&
        !(
          hubConnection.state === HubConnectionState.Disconnected ||
          hubConnection.state === HubConnectionState.Disconnecting
        )
      ) {
        console.debug(
          `Stopping ListingNotification connection (state: ${hubConnection.state}) with id: ${hubConnection.connectionId}`
        );
        hubConnection.stop();
        setHubConnection(undefined);
      }
    };
  }, [activeAccountWebClientConfig, hubConnection, logEvent]);

  return (
    <ListingNotificationContext.Provider
      value={{
        hubConnection,
      }}
    >
      {children}
    </ListingNotificationContext.Provider>
  );
}
